import React, { useState, useEffect } from 'react';
import './Payments.css';
import { useAuth } from '../../Security/AuthProvider';
import { useNotification } from '../../Utils/Commons/Notification/NotificationContext';

const Payments = () => {
  const { getCredentials } = useAuth();
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const { notifyError, notifySuccess } = useNotification();

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'metrics') {
      fetchMetrics();
    }
  }, [activeTab]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await fetch('/payments/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: 50,
          offset: 0
        }),
      });

      const data = await response.json();
      if (data.success) {
        setInvoices(data.data.invoices);
      } else {
        notifyError(data.error);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des factures:', err);
      notifyError('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await fetch('/payments/metrics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          period: '6' // 6 derniers mois
        }),
      });

      const data = await response.json();
      if (data.success) {
        setMetrics(data.data);
      } else {
        notifyError(data.error);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des métriques:', err);
      notifyError('Erreur lors du chargement des métriques');
    } finally {
      setLoading(false);
    }
  };

  const generateAllInvoices = async () => {
    try {
      setLoading(true);
      const response = await fetch('/payments/generate-all-invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();
      if (data.success) {
        notifySuccess(`✅ ${data.data.summary.success} factures générées, ${data.data.summary.failures} échecs`);
        if (activeTab === 'invoices') {
          fetchInvoices();
        }
      } else {
        notifyError(data.error);
      }
    } catch (err) {
      console.error('Erreur lors de la génération des factures:', err);
      notifyError('Erreur lors de la génération des factures');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'linear-gradient(135deg, #28a745, #20c997)';
      case 'sent': return 'linear-gradient(135deg, #ffc107, #e0a800)';
      case 'pending': return 'linear-gradient(135deg, #6c757d, #495057)';
      case 'failed': return 'linear-gradient(135deg, #dc3545, #c82333)';
      default: return 'linear-gradient(135deg, #6c757d, #495057)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid': return 'Payée';
      case 'sent': return 'Envoyée';
      case 'pending': return 'En attente';
      case 'failed': return 'Échec';
      default: return status;
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const AnimatedChart = ({ data, title, color }) => {
    const maxValue = Math.max(...data.map(item => item.value));
    
    return (
      <div className="animated-chart">
        <h4 className="chart-title">{title}</h4>
        <div className="chart-container">
          {data.map((item, index) => (
            <div key={index} className="chart-bar-container">
              <div className="chart-bar-label">{item.label}</div>
              <div className="chart-bar-wrapper">
                <div 
                  className="chart-bar"
                  style={{
                    height: `${(item.value / maxValue) * 100}%`,
                    background: color,
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <div className="chart-bar-value">
                    {typeof item.value === 'number' ? formatAmount(item.value) : item.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading && !invoices.length && !metrics) {
    return (
      <div className="payments-container">
        <div className="payments-loading">
          <div className="loading-spinner"></div>
          <p>Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payments-container">
      <div className="payments-header">
        <h1>💳 Gestion des Paiements</h1>
        <p>Tableau de bord avancé pour la facturation et le suivi des paiements</p>
      </div>

      <div className="payments-actions">
        <button 
          className="btn-primary"
          onClick={generateAllInvoices}
          disabled={loading}
        >
          📋 Générer toutes les factures
        </button>
      </div>

      <div className="payments-tabs">
        <button 
          className={`tab-button ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          📋 Factures
        </button>
        <button 
          className={`tab-button ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          📊 Tableau de bord
        </button>
      </div>

      <div className="payments-content">
        {activeTab === 'invoices' && (
          <div className="invoices-section">
            <h2>Factures récentes</h2>
            {invoices.length === 0 ? (
              <div className="no-invoices">
                <p>Aucune facture trouvée</p>
              </div>
            ) : (
              <div className="invoices-table">
                <div className="table-header">
                  <div>Entreprise</div>
                  <div>Période</div>
                  <div>RDV</div>
                  <div>Montant</div>
                  <div>Statut</div>
                  <div>Actions</div>
                </div>
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="table-row">
                    <div className="company-name">
                      {invoice.company_name}
                    </div>
                    <div>{invoice.period}</div>
                    <div>{invoice.rdv_count}</div>
                    <div className="amount">
                      {formatAmount(invoice.total_amount)}
                    </div>
                    <div>
                      <span 
                        className="status-badge" 
                        style={{ background: getStatusColor(invoice.status) }}
                      >
                        {getStatusLabel(invoice.status)}
                      </span>
                    </div>
                    <div className="actions">
                      {invoice.invoice_url && (
                        <a 
                          href={invoice.invoice_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-link"
                        >
                          Voir
                        </a>
                      )}
                      {invoice.pdf_url && (
                        <a 
                          href={invoice.pdf_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-link"
                        >
                          PDF
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'metrics' && metrics && (
          <div className="metrics-section">
            <h2>📊 Tableau de bord financier</h2>
            
            <div className="metrics-cards">
              <div className="metric-card">
                <div className="metric-icon">💰</div>
                <div className="metric-content">
                  <h3>Revenus totaux</h3>
                  <div className="metric-value">
                    {formatAmount(metrics.stats.total_amount || 0)}
                  </div>
                  <div className="metric-trend">
                    <span className="trend-indicator positive">+12%</span>
                    vs mois précédent
                  </div>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">✅</div>
                <div className="metric-content">
                  <h3>Revenus payés</h3>
                  <div className="metric-value">
                    {formatAmount(metrics.stats.paid_amount || 0)}
                  </div>
                  <div className="metric-trend">
                    <span className="trend-indicator positive">+8%</span>
                    taux de paiement
                  </div>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">⏳</div>
                <div className="metric-content">
                  <h3>En attente</h3>
                  <div className="metric-value">
                    {formatAmount(metrics.stats.pending_amount || 0)}
                  </div>
                  <div className="metric-trend">
                    <span className="trend-indicator neutral">-3%</span>
                    vs mois précédent
                  </div>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">📋</div>
                <div className="metric-content">
                  <h3>Factures totales</h3>
                  <div className="metric-value">
                    {metrics.stats.total_invoices || 0}
                  </div>
                  <div className="metric-trend">
                    <span className="trend-indicator positive">+15%</span>
                    nouvelles factures
                  </div>
                </div>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-section">
                <AnimatedChart
                  title="📈 Revenus mensuels (6 derniers mois)"
                  data={metrics.monthly_revenue.map(month => ({
                    label: month.month,
                    value: month.total_revenue
                  }))}
                  color="linear-gradient(135deg, #28a745, #20c997)"
                />
              </div>

              <div className="chart-section">
                <AnimatedChart
                  title="🏆 Top 5 Entreprises"
                  data={metrics.top_companies.slice(0, 5).map((company, index) => ({
                    label: company.company_name,
                    value: company.total_revenue
                  }))}
                  color="linear-gradient(135deg, #ffc107, #e0a800)"
                />
              </div>
            </div>

            <div className="financial-summary">
              <div className="summary-stats">
                <div className="stat-block">
                  <div className="stat-icon">💹</div>
                  <div className="stat-content">
                    <div className="stat-label">Taux de conversion</div>
                    <div className="stat-value">
                      {metrics.stats.total_invoices > 0 
                        ? Math.round((metrics.stats.paid_amount / metrics.stats.total_amount) * 100)
                        : 0}%
                    </div>
                  </div>
                </div>
                
                <div className="stat-block">
                  <div className="stat-icon">⚡</div>
                  <div className="stat-content">
                    <div className="stat-label">Facture moyenne</div>
                    <div className="stat-value">
                      {formatAmount(metrics.stats.average_invoice || 0)}
                    </div>
                  </div>
                </div>
                
                <div className="stat-block">
                  <div className="stat-icon">🎯</div>
                  <div className="stat-content">
                    <div className="stat-label">Croissance mensuelle</div>
                    <div className="stat-value">+12.5%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payments; 